// TrophyClaimGate — global watcher that auto-pops the Trophy Claim modal the
// moment a student crosses a 100 / 500 / 1000 / 5000-badge milestone.
//
// Behaviour:
//   • Polls GET /student/badges/trophies/pending-claim on mount + every 90s.
//   • If a pending unclaimed trophy is returned:
//       – Auto-opens the address-collection modal exactly once per session.
//       – Renders a persistent floating notification bar (bottom-right of
//         the viewport, above the sticker board) until the student claims
//         or dismisses it. Click → re-open modal.
//   • On successful claim, refetches so the bar disappears and the next
//     pending trophy (if any) auto-pops in the same session.
//
// Mounted once from StudentApp.jsx — independent of which sub-route the
// student is on, so even mid-lesson they'll see "Your trophy is ready!".
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from './studentApi';
import TrophyClaimModal from './TrophyClaimModal';
import { Trophy, X, ChevronRight } from 'lucide-react';

const POLL_MS = 90_000;             // 90s — light enough to be always-on
const SESSION_KEY = 'desky_trophy_autopop_session';

export default function TrophyClaimGate() {
  const location = useLocation();
  const [pending, setPending] = useState(null);     // { ...order, trophy: {...} }
  const [pendingCount, setPendingCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [barHidden, setBarHidden] = useState(false); // user X'd the bar this view
  const pollRef = useRef(null);
  const autoPopRef = useRef(false);

  const refetch = useCallback(async () => {
    try {
      const r = await api.get('/student/badges/trophies/pending-claim');
      const p = r?.pending || null;
      setPending(p);
      setPendingCount(r?.pending_count || 0);
      // Auto-pop ONCE per browser session per trophy id
      if (p && !autoPopRef.current) {
        const seen = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]');
        if (!seen.includes(p.id)) {
          setModalOpen(true);
          autoPopRef.current = true;
          sessionStorage.setItem(SESSION_KEY, JSON.stringify([...seen, p.id]));
        }
      }
    } catch { /* silent — heartbeat-style fetch */ }
  }, []);

  useEffect(() => {
    refetch();
    pollRef.current = setInterval(refetch, POLL_MS);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [refetch]);

  // Reset the "X dismissed" state whenever the student lands on a new route —
  // makes the notification bar gently re-appear in the next view if still pending.
  useEffect(() => { setBarHidden(false); }, [location.pathname]);

  const handleClaimed = () => {
    setModalOpen(false);
    setBarHidden(false);
    refetch();
  };

  if (!pending) return null;

  const trophy = pending.trophy || {};

  return (
    <>
      {/* ── Floating notification bar (bottom-right) ─────────────────────── */}
      {!modalOpen && !barHidden && (
        <div
          data-testid="trophy-claim-bar"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] max-w-md w-[calc(100%-32px)] rounded-2xl shadow-2xl border-2 backdrop-blur"
          style={{
            background: 'rgba(255,255,255,0.96)',
            borderColor: trophy.color || '#34A853',
            boxShadow: `0 12px 40px ${(trophy.color || '#34A853')}55`,
          }}>
          <div className="flex items-stretch">
            {/* Trophy face */}
            <button onClick={() => setModalOpen(true)}
              data-testid="trophy-claim-bar-open"
              className="flex-1 flex items-center gap-3 px-4 py-3 text-left hover:bg-black/5 rounded-l-2xl transition-colors">
              <div className="text-3xl flex-shrink-0 animate-pulse" aria-hidden>
                {trophy.icon || '🏆'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-extrabold uppercase tracking-widest"
                  style={{ color: trophy.color || '#0f172a' }}>
                  {pendingCount > 1
                    ? `${pendingCount} trophies ready · tap to claim`
                    : 'Your trophy is ready!'}
                </div>
                <div className="text-sm font-bold truncate text-slate-900">
                  {trophy.name || 'Deskey Plaque'} — confirm your delivery address
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </button>
            {/* Dismiss (visually subtle, not destructive — bar comes back next route) */}
            <button onClick={() => setBarHidden(true)}
              data-testid="trophy-claim-bar-dismiss"
              aria-label="Hide for now"
              className="px-3 border-l hover:bg-black/5 rounded-r-2xl"
              style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>
        </div>
      )}

      {/* ── Modal (auto-popped or click-opened) ─────────────────────────── */}
      {modalOpen && (
        <TrophyClaimModal
          trophy={{
            id: pending.trophy_id,
            name: trophy.name,
            icon: trophy.icon,
            color: trophy.color,
            desc: trophy.desc,
          }}
          onClose={() => setModalOpen(false)}
          onClaimed={handleClaimed} />
      )}
    </>
  );
}

// Tiny header pill — optional render in StudentApp's top bar.
export function TrophyClaimHeaderPill() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get('/student/badges/trophies/pending-claim');
        if (!cancelled) setCount(r?.pending_count || 0);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!count) return null;
  return (
    <a href="/student/badges"
      data-testid="trophy-claim-header-pill"
      className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all hover:-translate-y-0.5"
      style={{
        borderColor: '#F0A024',
        background: 'linear-gradient(135deg,#FFD70022,#F0A02411)',
        color: '#7a4d00',
      }}>
      <Trophy className="w-3.5 h-3.5" />
      <span>{count} ready</span>
    </a>
  );
}
